import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateLoanActivity } from '@/hooks/lib/invalidate-loan-activity';
import { api } from '@/lib/api';
import type {
  ActivityLogEntry,
  Comment,
  Loan,
  LoanAssignment,
  LoanStatus,
  PaginatedResponse,
  RepaymentScheduleItem,
} from '@/types';

// ── Board (loans grouped by status) ─────────────────────

export type BoardData = Record<LoanStatus, Loan[]>;

async function fetchBoard(): Promise<BoardData> {
  const { data } = await api.get<BoardData>('/loans/board');
  return data;
}

export function useLoanBoard() {
  return useQuery({
    queryKey: ['loans', 'board'],
    queryFn: fetchBoard,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: 2,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 10000),
    staleTime: 30_000,
  });
}

// ── Loan list (paginated + filterable) ──────────────────

export interface LoanListParams {
  page?: number;
  limit?: number;
  status?: LoanStatus;
  borrowerId?: string;
  assignedTo?: string;
  search?: string;
  loanType?: string;
  sortBy?: string;
  sortDir?: string;
}

async function fetchLoans(params: LoanListParams): Promise<PaginatedResponse<Loan>> {
  const { data } = await api.get<PaginatedResponse<Loan>>('/loans', { params });
  return data;
}

export function useLoanList(params: LoanListParams = {}) {
  return useQuery({
    queryKey: ['loans', 'list', params],
    queryFn: () => fetchLoans(params),
    retry: 2,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 10000),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function useInfiniteLoanList(params: Omit<LoanListParams, 'page'> = {}) {
  return useInfiniteQuery({
    queryKey: ['loans', 'infinite-list', params],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchLoans({ ...params, page: pageParam }),
    getNextPageParam: lastPage =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    retry: 2,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 10000),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

// ── Single loan ─────────────────────────────────────────

async function fetchLoan(id: string): Promise<Loan> {
  const { data } = await api.get<Loan>(`/loans/${id}`);
  return data;
}

export function useLoan(id: string) {
  return useQuery({
    queryKey: ['loans', id],
    queryFn: () => fetchLoan(id),
    enabled: !!id,
    retry: 2,
    staleTime: 30_000,
  });
}

// ── Status transition ───────────────────────────────────

interface TransitionInput {
  loanId: string;
  status: LoanStatus;
  reason?: string;
  effectiveDate?: string;
  notifyBorrower?: boolean;
}

async function transitionStatus({
  loanId,
  status,
  reason,
  effectiveDate,
  notifyBorrower,
}: TransitionInput): Promise<Loan> {
  const { data } = await api.patch<Loan>(`/loans/${loanId}/status`, {
    status,
    reason,
    effectiveDate,
    notifyBorrower,
  });
  return data;
}

export function useTransitionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: transitionStatus,
    onMutate: async vars => {
      await qc.cancelQueries({ queryKey: ['loans', vars.loanId] });
      const previousLoan = qc.getQueryData<Loan>(['loans', vars.loanId]);

      // Optimistically update the loan status
      if (previousLoan) {
        qc.setQueryData<Loan>(['loans', vars.loanId], {
          ...previousLoan,
          status: vars.status,
        });
      }
      return { previousLoan };
    },
    onError: (_err, vars, context) => {
      if (context?.previousLoan) {
        qc.setQueryData(['loans', vars.loanId], context.previousLoan);
      }
    },
    onSettled: (_data, _err, vars) => {
      // Targeted invalidation: status change affects this loan + board/list views
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', 'list'] });
      qc.invalidateQueries({ queryKey: ['loans', 'board'], exact: true });
      if (vars.notifyBorrower !== false) {
        qc.invalidateQueries({ queryKey: ['email-loan'] });
      }
      invalidateLoanActivity(qc, vars.loanId);
    },
  });
}

// ── Update loan ─────────────────────────────────────────

export interface UpdateLoanInput {
  loanType?: string;
  principal?: number;
  interestRate?: number;
  defaultRate?: number | null;
  termMonths?: number;
  paymentFrequency?: string;
  establishmentFee?: number | null;
  brokerageFee?: number | null;
  legalCosts?: number | null;
  legalCostsRecovered?: boolean;
  otherCosts?: number | null;
  otherCostsDescription?: string | null;
  borrowerId?: string;
  referrerId?: string | null;
  settlementDate?: string | null;
  establishmentFeeRetained?: number | null;
  brokerageFeeRetained?: number | null;
  referrerCommission?: number | null;
  internalReference?: string | null;
  lawFirmReference?: string | null;
  interestMethod?: string;
  flatInterestAmount?: number | null;
  externalReference?: string | null;
}

async function updateLoan(loanId: string, input: UpdateLoanInput): Promise<Loan> {
  const { data } = await api.patch<Loan>(`/loans/${loanId}`, input);
  return data;
}

export function useUpdateLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, ...input }: UpdateLoanInput & { loanId: string }) =>
      updateLoan(loanId, input),
    onSuccess: (_data, vars) => {
      // Targeted: update only this loan's detail + list view (board rarely changes on edit)
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', 'list'] });
      invalidateLoanActivity(qc, vars.loanId);
    },
  });
}

// ── Generate schedule ───────────────────────────────────

async function generateSchedule(loanId: string): Promise<RepaymentScheduleItem[]> {
  const { data } = await api.post<RepaymentScheduleItem[]>(`/loans/${loanId}/schedule`);
  return data;
}

export function useGenerateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: generateSchedule,
    onSuccess: (_data, loanId: string) => {
      qc.invalidateQueries({ queryKey: ['loans', loanId], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'cashflows'], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'npv'] });
      qc.invalidateQueries({ queryKey: ['loans', loanId, 'yield'], exact: true });
      invalidateLoanActivity(qc, loanId);
    },
  });
}

// ── Comments ────────────────────────────────────────────

async function fetchComments(loanId: string): Promise<Comment[]> {
  const { data } = await api.get<Comment[]>(`/loans/${loanId}/comments`);
  return data;
}

export function useComments(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'comments'],
    queryFn: () => fetchComments(loanId),
    enabled: !!loanId,
  });
}

interface AddCommentInput {
  loanId: string;
  body: string;
}

async function addComment({ loanId, body }: AddCommentInput): Promise<Comment> {
  const { data } = await api.post<Comment>(`/loans/${loanId}/comments`, { body });
  return data;
}

export function useAddComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: addComment,
    onMutate: async vars => {
      await qc.cancelQueries({ queryKey: ['loans', vars.loanId, 'comments'] });
      const previousComments = qc.getQueryData<Comment[]>(['loans', vars.loanId, 'comments']);

      // Optimistically add the comment for instant UI feedback
      if (previousComments) {
        const optimistic: Comment = {
          id: `optimistic-${Date.now()}`,
          body: vars.body,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as Comment;
        qc.setQueryData<Comment[]>(
          ['loans', vars.loanId, 'comments'],
          [...previousComments, optimistic],
        );
      }
      return { previousComments };
    },
    onError: (_err, vars, context) => {
      if (context?.previousComments) {
        qc.setQueryData(['loans', vars.loanId, 'comments'], context.previousComments);
      }
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'comments'] });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'activity'] });
    },
  });
}

// ── Activity log ────────────────────────────────────────

async function fetchActivity(loanId: string): Promise<ActivityLogEntry[]> {
  const { data } = await api.get<ActivityLogEntry[]>(`/loans/${loanId}/activity`);
  return data;
}

export function useActivity(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'activity'],
    queryFn: () => fetchActivity(loanId),
    enabled: !!loanId,
  });
}

// ── Assignments ─────────────────────────────────────────

async function fetchAssignments(loanId: string): Promise<LoanAssignment[]> {
  const { data } = await api.get<LoanAssignment[]>(`/loans/${loanId}/assignments`);
  return data;
}

export function useAssignments(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'assignments'],
    queryFn: () => fetchAssignments(loanId),
    enabled: !!loanId,
  });
}

// ── Interest accrual ────────────────────────────────

export interface InterestSummary {
  loanId: string;
  loanNumber: string;
  status: string;
  principal: number;
  standardRate: number;
  defaultRate: number | null;
  effectiveRate: number;
  isDefaultRateApplied: boolean;
  dailyAccrual: number;
  monthlyAccrual: number;
  totalAccruedInterest: number;
  totalPayments: number;
  principalOutstanding: number;
  outstandingInterest: number;
  totalOwed: number;
  recentAccruals: {
    id: string;
    amount: number;
    date: string;
    reference: string;
    description: string;
  }[];
}

export interface LoanAccrualSummary {
  date: string;
  loansProcessed: number;
  totalInterestAccrued: number;
  defaultLoansProcessed: number;
  errors: number;
  results: Array<{
    loanId: string;
    loanNumber: string;
    accrualDate: string;
    principal: number;
    rate: number;
    dailyInterest: number;
    isDefaultRate: boolean;
    transactionId: string | null;
    accrualRecordId?: string | null;
    alreadyAccrued?: boolean;
    error?: string;
  }>;
}

export function useInterestSummary(loanId: string) {
  return useQuery<InterestSummary>({
    queryKey: ['loans', loanId, 'interest'],
    queryFn: () => api.get(`/loans/${loanId}/interest`).then(r => r.data),
    enabled: !!loanId,
  });
}

export interface RunAccrualInput {
  loanId?: string;
  date?: string;
}

export function useRunAccrual() {
  const qc = useQueryClient();
  return useMutation<LoanAccrualSummary, unknown, RunAccrualInput | undefined>({
    mutationFn: input => {
      if (input?.loanId) {
        return api
          .post(`/loans/${input.loanId}/accrual/run`, input.date ? { date: input.date } : {})
          .then(r => r.data);
      }

      return api.post('/loans/accrual/run').then(r => r.data);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['loans'] });
      if (vars?.loanId) {
        qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'interest'], exact: true });
        qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'accruals'], exact: true });
        qc.invalidateQueries({ queryKey: ['loans', vars.loanId], exact: true });
      }
    },
  });
}

// ── Restructuring ───────────────────────────────────────

export interface RestructureInput {
  principal: number;
  interestRate: number;
  defaultRate?: number;
  termMonths: number;
  paymentFrequency?: string;
  loanType?: string;
  reason: string;
  userId: string;
  capitaliseInterest?: boolean;
  establishmentFee?: number;
}

export interface RestructureResult {
  originalLoan: { id: string; loanNumber: string; status: string };
  newLoan: Loan;
  restructureDetails: Record<string, any>;
}

export interface RestructureChainItem {
  id: string;
  loanNumber: string;
  status: LoanStatus;
  principal: number;
  interestRate: number;
  termMonths: number;
  restructureDate: string | null;
  restructureReason: string | null;
  direction: 'ancestor' | 'current' | 'descendant';
}

async function restructureLoan(
  loanId: string,
  input: RestructureInput,
): Promise<RestructureResult> {
  const { data } = await api.post<RestructureResult>(`/loans/${loanId}/restructure`, input);
  return data;
}

export function useRestructureLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, ...input }: RestructureInput & { loanId: string }) =>
      restructureLoan(loanId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useRestructureChain(loanId: string) {
  return useQuery<RestructureChainItem[]>({
    queryKey: ['loans', loanId, 'restructure-chain'],
    queryFn: () => api.get(`/loans/${loanId}/restructure-chain`).then(r => r.data),
    enabled: !!loanId,
  });
}

// ── Status history ──────────────────────────────────────

export interface StatusTransition {
  id: string;
  loanId: string;
  fromStatus: LoanStatus;
  toStatus: LoanStatus;
  changedBy: string;
  reason?: string;
  createdAt: string;
  user?: { id: string; firstName: string; lastName: string };
}

async function fetchStatusHistory(loanId: string): Promise<StatusTransition[]> {
  const { data } = await api.get<StatusTransition[]>(`/loans/${loanId}/status-history`);
  return data;
}

export function useStatusHistory(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'status-history'],
    queryFn: () => fetchStatusHistory(loanId),
    enabled: !!loanId,
  });
}
